import { Box, Chip, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { SCUIButton, SCUINoRecordComponent } from '../../../../../atomic/atoms';
import { OperatorCommand } from '../../../../../services/command';
import { OperatorQuery } from '../../../../../services/query';
import { LUR } from '../../listUserRoles.classes';

function SelectOperatorModal({ selectedUser }: any) {
    const { t } = useTranslation();
    const [value, setValue] = useState('');
    const [selectedOperators, setselectedOperators] = useState<any>([]);
    const [operators, setOperators] = useState<any>();
    const [loading, setLoading] = useState(false);

    const handleSendTransferRole = () => {
        const req = {
            SourceApplicationId: selectedUser.applicationUserId,
            TargetApplicationIds: selectedOperators.map((item: any) => item.applicationUserId),
        };
        OperatorCommand.TransferRole(req)
            .then(() => {
                Swal.fire(t('general.added'), ' ', 'success');
            })
            .catch((error: any) => {
                console.log('Hata :>> ', error);
            });
    };

    useEffect(() => {
        GetOperatorList();
    }, []);

    const GetOperatorList = async () => {
        try {
            if (value !== '' && value !== undefined && value !== null && value.length > 2) {
                setLoading(true);
                const params: any = {};
                params.username = value;
                params.status = 'all';
                await OperatorQuery.GetOperatorFindByUserName(params).then((res) => {
                    setOperators(res.model.data);
                });
            }
        } catch (error) {
            console.log('Hata :>> ', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOperatorAddList = (user: any) => {
        if (
            !selectedOperators ||
            !selectedOperators.find((item: any) => item.applicationUserId === user.applicationUserId)
        ) {
            setselectedOperators([...selectedOperators, user]);
        }
    };

    const handleRemoveSelected = (applicationUserId: number) => {
        setselectedOperators(
            selectedOperators.filter((item: any) => item.applicationUserId !== applicationUserId)
        );
    };

    return (
        <Box
            className={LUR.selectModal}
            data-application-user-id={selectedUser?.applicationUserId}
            data-selected-count={selectedOperators.length}
            sx={{ p: 3 }}
        >
            <Typography
                className={LUR.selectModalTitle}
                sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#6B7280', mb: 1.5 }}
            >
                {t('authorizationManagement.usersToTransferRole')}
            </Typography>

            <Box
                className={LUR.selectModalSelectedList}
                data-selected-count={selectedOperators.length}
                sx={{
                    minHeight: 48,
                    p: 1.5,
                    borderRadius: '12px',
                    border: '1px dashed #D1D5DB',
                    backgroundColor: selectedOperators.length > 0 ? '#F9FAFB' : '#FAFAFA',
                    mb: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                {selectedOperators.length > 0 ? (
                    selectedOperators.map((item: any, index: number) => (
                        <Chip
                            key={index}
                            className={LUR.selectModalSelectedChip}
                            label={item.username}
                            data-application-user-id={item.applicationUserId}
                            data-user-ref={item.userRef}
                            data-username={item.username}
                            onDelete={() => handleRemoveSelected(item.applicationUserId)}
                            deleteIcon={<CloseRoundedIcon />}
                            sx={{
                                fontWeight: 600,
                                backgroundColor: '#EFF6FF',
                                color: '#2563eb',
                                '& .MuiChip-deleteIcon': { color: '#2563eb' },
                            }}
                        />
                    ))
                ) : (
                    <Typography variant="body2" sx={{ color: '#9CA3AF', fontStyle: 'italic', px: 0.5 }}>
                        —
                    </Typography>
                )}
            </Box>

            {selectedOperators && selectedOperators.length > 0 && (
                <Box className={LUR.selectModalActions} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                    <SCUIButton
                        className={LUR.btnSelectClear}
                        type={'secondary'}
                        sizeType={'fixed'}
                        size={'s'}
                        text={t('general.clearList')}
                        onClick={() => setselectedOperators([])}
                    />
                    <SCUIButton
                        className={LUR.btnSelectTransfer}
                        type={'primary'}
                        sizeType={'fixed'}
                        size={'s'}
                        text={t('general.transferRole')}
                        onClick={handleSendTransferRole}
                    />
                </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <PersonSearchRoundedIcon sx={{ color: '#2563eb', fontSize: 22, flexShrink: 0 }} />
                <TextField
                    className={LUR.inputSelectSearch}
                    size="small"
                    fullWidth
                    placeholder={t('authorizationManagement.listUser.placeholderWarning')}
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            GetOperatorList();
                        }
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
                <Tooltip title={t('general.search')}>
                    <IconButton
                        aria-label="search"
                        disabled={loading}
                        onClick={() => GetOperatorList()}
                        sx={{
                            border: '1px solid rgba(37, 99, 235, 0.25)',
                            backgroundColor: 'rgba(37, 99, 235, 0.06)',
                            color: '#2563eb',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(37, 99, 235, 0.14)',
                                transform: 'scale(1.08)',
                                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : <SearchRoundedIcon />}
                    </IconButton>
                </Tooltip>
            </Box>

            {operators ? (
                <Box className={LUR.selectModalTable} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {operators?.map((user: any) => (
                        <Box
                            key={user.userRef}
                            className={`${LUR.selectModalRow} ${LUR.selectModalUserItem}`}
                            data-application-user-id={user.applicationUserId}
                            data-user-ref={user.userRef}
                            data-username={user.username}
                            onClick={() => handleOperatorAddList(user)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1.25,
                            }}
                        >
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#2563eb' }}>
                                {user.username}
                            </Typography>
                            <Tooltip title={t('general.add')}>
                                <IconButton
                                    className={LUR.btnSelectAddOperator}
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOperatorAddList(user);
                                    }}
                                    sx={{
                                        color: '#2563eb',
                                        border: '1px solid #BFDBFE',
                                        backgroundColor: '#EFF6FF',
                                        borderRadius: '8px',
                                        '&:hover': { backgroundColor: '#DBEAFE' },
                                    }}
                                >
                                    <PersonAddRoundedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
            ) : (
                <SCUINoRecordComponent />
            )}
        </Box>
    );
}

export default SelectOperatorModal;
